/**
 * RegistrarEnvioManualModal (Iter 62)
 * -----------------------------------
 * Permite à vendedora registrar que ela enviou a confirmação de inscrição
 * manualmente pelo próprio Gmail/Outlook (quando o Resend bloqueia o destinatário).
 * Serve apenas pra manter o histórico da inscrição completo — não dispara envio.
 */
import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { CheckCircle2, Mail, X, Loader2 } from "lucide-react";
import api, { apiErrorMsg } from "../lib/api";
import { toast } from "sonner";

export default function RegistrarEnvioManualModal({ open, onClose, inscricaoId, destinatariosSugeridos = [], onDone }) {
  const [emails, setEmails] = useState([]);
  const [input, setInput] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setEmails(destinatariosSugeridos.filter((e) => e && e.includes("@")));
      setInput("");
      setObservacoes("");
    }
  }, [open, destinatariosSugeridos]);

  const addEmail = () => {
    const v = input.trim().replace(/,$/, "");
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error(`E-mail inválido: ${v}`);
      return;
    }
    if (emails.includes(v)) {
      setInput("");
      return;
    }
    setEmails([...emails, v]);
    setInput("");
  };

  const salvar = async () => {
    if (emails.length === 0) {
      toast.error("Informe pelo menos um destinatário");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/inscricoes/${inscricaoId}/registrar-envio-manual`, {
        email_destinatarios: emails,
        observacoes: observacoes.trim() || null,
      });
      toast.success(`✅ Envio manual registrado para ${emails.length} destinatário(s)`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao registrar"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="registrar-envio-manual-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            Registrar envio manual
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Marque aqui a confirmação que você enviou pelo seu Gmail/Outlook. Fica no histórico da inscrição pra ninguém reenviar sem necessidade.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Destinatário(s) que receberam</Label>
            <div
              className="min-h-[42px] flex flex-wrap items-center gap-1.5 p-2 border border-slate-300 rounded-md bg-white mt-1"
              data-testid="envio-manual-chips"
            >
              {emails.map((e) => (
                <span
                  key={e}
                  className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full pl-2 pr-1 py-0.5 text-xs"
                >
                  <Mail className="w-3 h-3" />
                  {e}
                  <button
                    type="button"
                    onClick={() => setEmails(emails.filter((x) => x !== e))}
                    className="ml-1 w-4 h-4 rounded-full bg-white hover:bg-red-100 hover:text-red-600 text-slate-500 flex items-center justify-center text-[10px]"
                    aria-label={`Remover ${e}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
              <input
                type="email"
                value={input}
                onChange={(ev) => setInput(ev.target.value)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === ",") { ev.preventDefault(); addEmail(); }
                  else if (ev.key === "Backspace" && !input && emails.length > 0) {
                    setEmails(emails.slice(0, -1));
                  }
                }}
                onBlur={addEmail}
                placeholder={emails.length === 0 ? "email@camaramunicipal.gov.br" : ""}
                className="flex-1 min-w-[180px] border-0 outline-0 text-sm bg-transparent"
                data-testid="envio-manual-input"
              />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Pressione Enter ou vírgula para adicionar cada endereço.
            </div>
          </div>

          <div>
            <Label>Observações (opcional)</Label>
            <Textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Ex.: enviei do Gmail da Juliana porque o sistema bloqueou"
              rows={3}
              className="mt-1 text-sm"
              data-testid="envio-manual-obs"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="envio-manual-cancelar">
            Cancelar
          </Button>
          <Button
            onClick={salvar}
            disabled={saving || emails.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="envio-manual-confirmar"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Marcar como enviado
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
